/** biome-ignore-all lint/suspicious/noExplicitAny: legacy auth/redux/model shapes */
/** biome-ignore-all lint/complexity/noExcessiveCognitiveComplexity: legacy nav/breadcrumb switch preserved */
/** biome-ignore-all lint/correctness/useExhaustiveDependencies: legacy dep lists preserved */
import { useAuth } from '@bb/session-manager';
import { Box, Flex } from '@rocket/ui';
import { useLocation, useParams } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import {
  BLACKBOILER_ADMIN_PERMISSION,
  CLAUSE_EDITOR_PERMISSION,
  COMPANY_ADMIN_PERMISSION,
  CONTRACTS_PERMISSION,
  FAST_TRACK_PERMISSION,
  METRICS_DASHBOARD_PERMISSION,
  METRICS_FASTTRACK_PERMISSION,
  PLAYBOOK_BUILDER_PERMISSION,
  POA_GEMS_PERMISSION,
  POA_INTAKE_PERMISSION,
  POA_REVIEW_PERMISSION,
} from '../../groups.config';
import { SetCurrentModelData } from '../../redux/actions/modelActions';
import { setErrMsg } from '../../redux/actions/snackbarActions';
import { GetModelBasics } from '../../requests/trainingModel';
import {
  ANALYTICS_ROUTE,
  CLAUSES_ROUTE,
  CONTRACTS_ROUTE,
  DOCSERVER_READONLY_ROUTE,
  DOCSERVER_ROUTE,
  FAST_TRACK_ROUTE,
  HOME_ROUTE,
  INTEGRATIONS_ROUTE,
  LLM_STATS_ROUTE,
  MODEL_EDIT_ROUTE,
  MODEL_ROUTE,
  POA_ROUTE,
  REPORTING_ROUTE,
  SETTINGS_ROUTE,
  TENANT_DASHBOARD_ROUTE,
  TEXT_INDEXER_URL,
  USERS_ROUTE,
} from '../../routes.config';
import { ExportButton } from '../ExportButton';
import AppBarBreadCrumb from './AppBarBreadCrumb';
import AppBarFull from './AppBarFull';

type AnyData = any;

/**
 * Ports MUI `<AppBar position="fixed"> + <Toolbar>`: fixed black bar, MUI
 * appBar zIndex (1100), Toolbar's 64px min-height + 24px side padding.
 */
const AppBarShell = ({ children }: { children: React.ReactNode }) => (
  <Box as="header" position="fixed" top="0" left="0" right="0" zIndex={1100} bg="black">
    <Flex minHeight="64px" paddingInline="24px" alignItems="center" width="100%">
      {children}
    </Flex>
  </Box>
);

function BaseAppBar() {
  const [company, setCompany] = useState<string | undefined>();
  const { pathname } = useLocation();
  const { config, user, auth } = useAuth() as AnyData;

  // strict: false allows accessing params from any matched route (needed for layout components)
  const { id = null } = useParams({ strict: false }) as AnyData;
  const dispatch = useDispatch();

  const modelData = useSelector((state: AnyData) => state.Model.currentModelData, shallowEqual);
  const availableSections = useSelector(
    (state: AnyData) => state.PlaybookBuilder.availableSections,
    shallowEqual
  );

  const setErr = (value: string) => dispatch(setErrMsg(value));

  useEffect(() => {
    if (config) {
      if (config.displayName) {
        setCompany(config.displayName);
      } else {
        setCompany(capitalize(config.name));
      }
    }
  }, [config]);

  useEffect(() => {
    if (id && !modelData) {
      // refresh model data basics asynchronously if unavailable and on a model path

      GetModelBasics(id)
        .then((model: AnyData) => {
          dispatch(SetCurrentModelData(model));
        })
        .catch((err: AnyData) => {
          setErr('Failed to retrieve model data');
          throw err;
        });
    }
  }, [id]);

  const capitalize = (str: string) =>
    str
      .toLowerCase()
      .split(' ')
      .map((s) => s.charAt(0).toUpperCase() + s.substring(1))
      .join(' ');

  const navList: Array<AnyData> = [{ label: 'Home', path: HOME_ROUTE, role: true }];

  if (config && user) {
    // Dynamically build the navigation list based on user permissions and config
    navList.push({
      label: 'Contracts',
      path: CONTRACTS_ROUTE,
      role: user?.permissions?.has(CONTRACTS_PERMISSION),
    });
    if (config.showClauseLibrary) {
      navList.push({
        label: 'Clause Library',
        path: CLAUSES_ROUTE,
        role: user?.permissions?.has(CLAUSE_EDITOR_PERMISSION),
      });
    }
    if (config.showPlaybookBuilder) {
      navList.push({
        label: 'Playbook Builder',
        path: MODEL_ROUTE,
        role: user?.permissions?.has(PLAYBOOK_BUILDER_PERMISSION),
      });
    }
    if (config.showFastTrack) {
      navList.push({
        label: 'Fast Track',
        path: FAST_TRACK_ROUTE,
        role: user?.permissions?.has(FAST_TRACK_PERMISSION),
      });
    }
    if (config.showPOA) {
      navList.push({
        label: 'PoA',
        path: POA_ROUTE,
        role:
          user?.permissions?.has(POA_INTAKE_PERMISSION) ||
          user?.permissions?.has(POA_GEMS_PERMISSION) ||
          user?.permissions?.has(POA_REVIEW_PERMISSION),
      });
    }
    if (config.showAnalytics) {
      navList.push({
        label: 'Analytics',
        path: ANALYTICS_ROUTE,
        role:
          user?.permissions?.has(METRICS_DASHBOARD_PERMISSION) ||
          user?.permissions?.has(METRICS_FASTTRACK_PERMISSION),
      });
    }
    if (config.showReportingDashboard) {
      navList.push({
        label: 'Reporting',
        path: REPORTING_ROUTE,
        role: user?.permissions?.has(BLACKBOILER_ADMIN_PERMISSION),
      });
    }
    if (config.showIntegrations) {
      navList.push({
        label: 'Integrations',
        path: INTEGRATIONS_ROUTE,
        role: user?.permissions?.has(BLACKBOILER_ADMIN_PERMISSION),
      });
    }
    navList.push({
      label: 'Users',
      path: USERS_ROUTE,
      role: user?.permissions?.has(COMPANY_ADMIN_PERMISSION),
    });
    navList.push({
      label: 'Settings',
      path: SETTINGS_ROUTE,
      role: user?.permissions?.has(BLACKBOILER_ADMIN_PERMISSION),
    });
  }

  if (user?.permissions?.has(BLACKBOILER_ADMIN_PERMISSION)) {
    navList.push({
      label: 'Admin',
      role: true,
      children: [
        ...(config?.useTenantManagement
          ? [{ label: 'Tenant Dashboard', path: TENANT_DASHBOARD_ROUTE }]
          : []),
        { label: 'LLM Stats', path: LLM_STATS_ROUTE },
        { label: 'Text Indexer', path: TEXT_INDEXER_URL, external: true },
      ],
    });
  }

  const playbookBuilderAnswerQuestionsLookupMap: Record<string, string> = {
    select: 'Answer Questions',
    status: 'Answer Questions',
  };
  if (availableSections) {
    availableSections.forEach(({ className, name }: AnyData) => {
      playbookBuilderAnswerQuestionsLookupMap[className] = name;
    });
  }

  // Extract userName for display. The OIDC/Auth0 profile is available on `auth`
  // independent of the redux `user` object — read it OUTSIDE the `if (user)`
  // gate so a valid profile still renders when redux `user` hasn't loaded yet.
  let userName = '';
  const profile = auth?.user?.profile;
  if (profile?.given_name && profile?.family_name) {
    userName = `${profile.given_name} ${profile.family_name}`;
  } else if (profile?.name) {
    userName = profile.name;
  } else if (profile?.given_name) {
    userName = profile.given_name;
  } else if (user) {
    // Fall back to API user object fields (toShortProfile returns fullName)
    if (user.fullName) {
      userName = user.fullName;
    } else if (user.name?.first) {
      userName = user.name.full || `${user.name.first} ${user.name.last || ''}`.trim();
    } else if (typeof user.name === 'string' && user.name) {
      userName = user.name;
    } else if (user.email) {
      // Fallback to email if no name fields available
      userName = user.email;
    }
  }

  const renderSwitch = () => {
    const playbookBuilderAnswerQuestionsRegex = new RegExp(
      `${MODEL_EDIT_ROUTE}/[0-9a-z]*/rules/aq/.*`,
      'g'
    );

    const breadcrumbs: Array<AnyData> = [];

    switch (true) {
      case DOCSERVER_ROUTE === pathname:
        breadcrumbs.push(navList[1]);
        breadcrumbs.push({ label: 'View Contract', path: pathname });
        return (
          <AppBarShell>
            <AppBarBreadCrumb breadCrumbs={breadcrumbs} buttonRight={<ExportButton />} />
          </AppBarShell>
        );
      case DOCSERVER_READONLY_ROUTE === pathname:
        breadcrumbs.push(navList[1]);
        breadcrumbs.push({ label: 'View Archived', path: pathname });
        return (
          <AppBarShell>
            <AppBarBreadCrumb breadCrumbs={breadcrumbs} />
          </AppBarShell>
        );
      case (pathname.match(playbookBuilderAnswerQuestionsRegex) || []).length > 0: {
        // SPECIAL ROUTING FOR ANSWER QUESTIONS PLAYBOOK BUILDER FLOW
        const splitPath = pathname.split('/rules/aq');

        if (splitPath.length !== 2) {
          // default if no path specified
          return (
            <AppBarShell>
              <AppBarFull
                pathname={pathname}
                navList={navList}
                companyName={company}
                userName={userName}
              />
            </AppBarShell>
          );
        }
        // e.g. /bbweb/model/edit/602fe1a3f554de005a02c371/rules/aq/select/example => ['select', 'example']
        const paths = (splitPath.at(-1) as string).split('/').filter((o) => o.length > 0);

        let gradualPath = `${splitPath[0]}/rules/aq`;
        // add core breadcrumb paths
        if (user?.permissions?.has(PLAYBOOK_BUILDER_PERMISSION)) {
          breadcrumbs.push({
            label: modelData ? modelData.name : '',
            path: `${splitPath[0]}`,
          });
        }
        if (paths.filter((path) => path === 'select' || path === 'status').length === 0) {
          // not on /select or /status pages
          breadcrumbs.push({ label: 'Answer Questions', path: `${gradualPath}/status` });
        }

        paths.forEach((path) => {
          gradualPath += `/${path}`;
          breadcrumbs.push({
            label: playbookBuilderAnswerQuestionsLookupMap[path],
            path: gradualPath,
          });
        });

        return (
          <AppBarShell>
            <AppBarBreadCrumb
              breadCrumbs={breadcrumbs}
              returnLabelText="Model"
              returnLabelLink={splitPath[0]}
            />
          </AppBarShell>
        );
      }
      default:
        return (
          <AppBarShell>
            <AppBarFull
              pathname={pathname}
              navList={navList}
              companyName={company}
              userName={userName}
            />
          </AppBarShell>
        );
    }
  };

  return !!auth?.user && !!user && renderSwitch();
}

export default BaseAppBar;
